import { AfterViewInit, Component, computed, ElementRef, inject, input, output, signal, untracked, viewChild } from '@angular/core';
import { KENDO_BUTTON, KENDO_CHIP } from '@progress/kendo-angular-buttons';
import { IconsModule, KENDO_SVGICON } from '@progress/kendo-angular-icons';
import { KENDO_CARD, KENDO_GRIDLAYOUT, KENDO_STACKLAYOUT } from '@progress/kendo-angular-layout';
import { FormInstanceDetailedDto, FormResponsesClient, UnityWorkflowState } from '@unity/clients';
import { AuthorizeForInternalDirective } from '@unity/directives/authorize-for-internal.directive';
import { AuthorizeDirective } from '@unity/directives/authorize.directive';
import { DateTimezonePipe } from '@unity/pipes/date-timezone.pipe';
import { DownloadService } from '@unity/services/download.service';
import { IconService } from '@unity/services/icon.service';
import { LayoutService } from '@unity/services/layout.service';
import { LinkSharedComponent } from '@unity/shared/composed/link-shared/link-shared.component';
import { ButtonDownloadSharedComponent } from '@unity/shared/presentation/button-download-shared/button-download-shared.component';
import { LinkButtonSharedComponent } from '@unity/shared/presentation/link-button-shared/link-button-shared.component';
import { take, tap } from 'rxjs';

@Component({
  selector: 'unity-card-form-instance-shared',
  standalone: true,
  imports: [
    KENDO_CARD,
    KENDO_GRIDLAYOUT,
    KENDO_STACKLAYOUT,
    KENDO_BUTTON,
    KENDO_CHIP,
    KENDO_SVGICON,
    IconsModule,
    DateTimezonePipe,
    AuthorizeDirective,
    AuthorizeForInternalDirective,
    ButtonDownloadSharedComponent,
    LinkButtonSharedComponent,
    LinkSharedComponent,
  ],
  templateUrl: './card-form-instance-shared.component.html',
  styleUrl: './card-form-instance-shared.component.scss',
})
export class CardFormInstanceSharedComponent implements AfterViewInit {
  private readonly formResponsesClient = inject(FormResponsesClient);
  private readonly iconService = inject(IconService);
  private readonly layoutService = inject(LayoutService);
  private readonly downloadService = inject(DownloadService);

  public readonly $highlightedRecordId = input.required<number | undefined>({ alias: 'highlightedRecordId' });
  public readonly $formInstance = input.required<FormInstanceDetailedDto>({ alias: 'formInstance' });

  private readonly $containerReference = viewChild.required<ElementRef>('container');

  protected downloadIcon = this.iconService.downloadIcon;
  protected changesRequestedState = UnityWorkflowState.ChangeRequested;

  protected readonly $isHighlighted = signal<boolean>(false);

  protected readonly $name = computed(() => {
    const formInstance = this.$formInstance();
    return `${formInstance.form_schedule.name} ${formInstance.id}`;
  });

  protected readonly $rowConfig = computed(() => {
    const orientation = this.layoutService.$orientation();
    return orientation === 'horizontal' ? ['2.5rem'] : ['1fr', '1fr', '1fr'];
  });

  protected readonly $colConfig = computed(() => {
    const orientation = this.layoutService.$orientation();
    return orientation === 'vertical' ? [] : ['1fr', '40%', '10rem'];
  });

  protected readonly $isDownloading = signal(false);

  public scrollToEvent = output();

  public ngAfterViewInit(): void {
    const formInstance = untracked(this.$formInstance);
    const highlightedRecordId = untracked(this.$highlightedRecordId);
    if (highlightedRecordId === formInstance.id) {
      this.$isHighlighted.set(true);
      this.scrollToEvent.emit();
      this.$containerReference().nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  protected onClickDownload(formInstance: FormInstanceDetailedDto) {
    if (formInstance.form_response == null) {
      // The button is only shown if can_download_pdf is true, which can only be true if there is a form response with an upload id.
      // So, this should never happen.
      throw new Error("Cannot download the pdf, the form hasn't been responded to yet.");
    }
    this.$isDownloading.set(true);
    this.formResponsesClient
      .downloadOneForAgreement(formInstance.agreement.id, formInstance.form_response.id)
      .pipe(
        take(1),
        tap(file => this.downloadService.saveFile(file))
      )
      .subscribe({
        complete: () => this.$isDownloading.set(false),
      });
  }
}
